import { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { PANEL_IDS } from '../../config/panelRegistry';
import { usePanelSettings } from '../../contexts/PanelSettingsContext';
import InfoHint from '../InfoHint';
import { AXIS, CATEGORICAL_PALETTE } from '../nodes/nodeChartTheme';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ErrorText, GranularitySelect, MetricToggle, MutedText, SELECT_CLASS } from './callTypesControls';
import {
  averageScore,
  buildEmployeeTrend,
  callsNoun,
  formatCount,
  formatPercent,
  formatScore,
  scoredCount
} from './callTypesModel';

const METRICS = [
  { id: 'calls', label: 'Звонки' },
  { id: 'share', label: 'Доля' },
  { id: 'score', label: 'Балл чек-листа' }
];

const colorOf = (index) => CATEGORICAL_PALETTE[index % CATEGORICAL_PALETTE.length];

const formatTooltipValue = (metric, value, row, dataKey) => {
  if (metric === 'share') {
    return `${Number(value).toFixed(1).replace('.', ',')}%`;
  }

  if (metric === 'score') {
    const scored = row[`scored_${String(dataKey).split('_').pop()}`];
    return `${formatScore(Number(value))} · оценено ${scored}`;
  }

  return `${formatCount(value)} ${callsNoun(value)}`;
};

/** Динамика сотрудников в выбранном срезе. Числа считает сервер, поэтому они верны при любом объёме. */
const EmployeeComparisonTrend = ({ dateRange, breakdown, scopeTitle, shareDescription }) => {
  const panels = usePanelSettings();
  const panelId = PANEL_IDS['ui:call-paths.employees'];
  const { employees, forms, formCode, onFormChange, granularity, onGranularityChange, isLoading, error } = breakdown;
  const [metric, setMetric] = useState('calls');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('all');

  const rows = useMemo(
    () => buildEmployeeTrend({ employees, dateRange, granularity, formCode }),
    [employees, dateRange, granularity, formCode]
  );
  const selectedEmployee = employees.find(({ employeeId }) => employeeId === selectedEmployeeId);
  const series = employees
    .map((employee, index) => ({ employee, index }))
    .filter(({ employee }) => !selectedEmployee || employee === selectedEmployee);
  const hasScores = series.some(({ employee }) => scoredCount(employee.scores, formCode) > 0);
  const formLabel = forms.find(({ code }) => code === formCode)?.label ?? 'Чек-лист';

  const caption = {
    calls: 'Число звонков каждого сотрудника с выбранным значением за период.',
    share: shareDescription,
    score: `Средний балл оценённых звонков по форме «${formLabel}» за каждый период.`
  }[metric];
  const hint = metric === 'score'
    ? 'Балл считается только по оценённым звонкам выбранной формы чек-листа. Если за период оценок нет, точка не показана.'
    : `${shareDescription} Здесь доля считается от звонков сотрудника, поэтому может немного отличаться от доли в списках выше: там она считается от звонков, обработанных анализом. Если в периоде нет звонков для расчёта, точка не показана.`;

  return (
    <Card data-panel-id={PANEL_IDS['ui:call-paths.employees']} className="min-w-0">
      <CardHeader className="flex flex-col gap-3 space-y-0 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <CardTitle className="text-base">
            {selectedEmployee ? selectedEmployee.employeeName : panels.title(panelId)}
          </CardTitle>
          <p className="mt-1 text-sm text-muted-foreground">{scopeTitle}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            data-ev="call_paths.employees.filter"
            aria-label="Сотрудник на графике"
            value={selectedEmployee?.employeeId ?? 'all'}
            onChange={(event) => setSelectedEmployeeId(event.target.value)}
            className={SELECT_CLASS}
          >
            <option value="all">Все сотрудники</option>
            {employees.map(({ employeeId, employeeName }) => (
              <option key={employeeId} value={employeeId}>
                {employeeName}
              </option>
            ))}
          </select>
          <GranularitySelect
            trackId="call_paths.employees.granularity"
            label="Шаг динамики сотрудников"
            value={granularity}
            onChange={onGranularityChange}
          />
          <MetricToggle trackId="call_paths.employees.metric" options={METRICS} value={metric} onChange={setMetric} />
          {metric === 'score' && forms.length > 1 && (
            <select
              data-ev="call_paths.employees.form"
              aria-label="Форма чек-листа"
              value={formCode ?? ''}
              onChange={(event) => onFormChange(event.target.value)}
              className={SELECT_CLASS}
            >
              {forms.map(({ code, label }) => (
                <option key={code} value={code}>
                  {label}
                </option>
              ))}
            </select>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <p className="flex items-center gap-1 text-xs text-muted-foreground">
          {caption}
          <InfoHint text={hint} label="Как считается показатель" trackId="call_paths.employees.hint" />
        </p>
        {error && <ErrorText>{error}</ErrorText>}
        {!error && isLoading && <MutedText>Загружаем динамику сотрудников…</MutedText>}
        {!error && !isLoading && employees.length === 0 && <MutedText>Нет звонков сотрудников с выбранными условиями.</MutedText>}
        {!error && !isLoading && employees.length > 0 && metric === 'score' && !hasScores && (
          <MutedText>За выбранный период нет оценённых звонков по этой форме чек-листа.</MutedText>
        )}
        {!error && !isLoading && employees.length > 0 && (metric !== 'score' || hasScores) && (
          <>
            <div className="mt-4 h-64 min-w-0">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={180}>
                <LineChart data={rows} margin={{ top: 10, right: 18, bottom: 8, left: -10 }}>
                  <CartesianGrid {...AXIS.grid} vertical={false} />
                  <XAxis dataKey="label" tick={AXIS.tick} minTickGap={18} />
                  <YAxis
                    width={45}
                    allowDecimals={metric !== 'calls'}
                    domain={{ share: [0, 100], score: [0, 10], calls: [0, 'auto'] }[metric]}
                    tick={AXIS.tick}
                    tickFormatter={metric === 'share' ? (value) => `${value}%` : undefined}
                  />
                  <Tooltip
                    wrapperStyle={AXIS.tooltipWrapperStyle}
                    formatter={(value, name, item) => [formatTooltipValue(metric, value, item.payload, item.dataKey), name]}
                  />
                  {series.map(({ employee, index }) => (
                    <Line
                      key={employee.employeeId}
                      type="linear"
                      dataKey={`${metric}_${index}`}
                      name={employee.employeeName}
                      stroke={colorOf(index)}
                      strokeWidth={2.5}
                      dot={{ r: 2 }}
                      activeDot={{ r: 4 }}
                      isAnimationActive={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 border-t pt-3">
              {/* Селект формы виден не всегда, а без названия балл ни к чему не привязан. */}
              {forms.length > 0 && <p className="mb-2 text-xs text-muted-foreground">Балл — по форме «{formLabel}»</p>}
              <div className="max-h-36 overflow-y-auto" role="group" aria-label="Сотрудники на графике">
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {employees.map((employee, index) => {
                    const isSelected = selectedEmployeeId === employee.employeeId;
                    const scored = scoredCount(employee.scores, formCode);

                    return (
                      <button
                        key={employee.employeeId}
                        type="button"
                        data-ev="call_paths.employees.select"
                        aria-pressed={isSelected}
                        onClick={() => setSelectedEmployeeId(isSelected ? 'all' : employee.employeeId)}
                        className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-left text-xs transition-colors hover:bg-blue-50 dark:hover:bg-blue-950/30 ${
                          isSelected ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30' : selectedEmployee ? 'opacity-55' : ''
                        }`}
                      >
                        <span className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: colorOf(index) }} />
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{employee.employeeName}</span>
                          <span className="block text-muted-foreground">
                            {formatCount(employee.calls)} {callsNoun(employee.calls)} · {formatPercent(employee.calls, employee.denominatorCalls)}
                          </span>
                          <span className="block text-muted-foreground">
                            Балл: {scored > 0 ? `${formatScore(averageScore(employee.scores, formCode))} · оценено ${scored}` : 'нет оценок'}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeComparisonTrend;
